import { useEffect, useRef, useState } from "react";
import { Html, siteData } from "../copy";

const STEP_MS = 4200;

/**
 * The action-and-result reel (the prototype's arBuild/arShow): one recommended move at a time,
 * advancing every 4.2 s. It only runs while it is near the viewport (an IntersectionObserver with
 * a 200 px margin), pauses under the pointer, and doesn't advance on its own when the user prefers
 * reduced motion.
 */
export function ActionReel() {
  const items = siteData<[string, string, string, string][]>("home.ar.items");
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [hover, setHover] = useState(false);
  const [manual, setManual] = useState(0);
  const stage = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = stage.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver((es) => es.forEach((e) => setVisible(e.isIntersecting)), {
      rootMargin: "200px",
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!visible || hover || reduce || items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), STEP_MS);
    return () => clearInterval(t);
  }, [visible, hover, items.length, manual]);

  return (
    <div className="arwrap">
      <div
        className="arstage"
        id="arstage"
        ref={stage}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <span className="arglow" />
        {items.map((n, i) => (
          <div className={`arslide${i === idx ? " on" : ""}`} key={n[1]} aria-hidden={i !== idx}>
            <Html className="kk" html={n[0]} />
            <Html as="p" className="act" html={n[1]} />
            <svg className="arw" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 3 L12 19 M5.5 12.5 L12 19 L18.5 12.5"
                stroke="#F7C733"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Html as="div" className="num" html={n[2]} />
            <Html as="div" className="sub" html={n[3]} />
          </div>
        ))}
      </div>
      <div className="ardots" id="ardots">
        {items.map((n, i) => (
          <button
            type="button"
            key={n[1]}
            className={`ardot${i === idx ? " on" : ""}`}
            aria-label={n[0]}
            aria-pressed={i === idx}
            onClick={() => {
              setIdx(i);
              setManual((m) => m + 1);
            }}
          />
        ))}
      </div>
    </div>
  );
}
